import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';
import { getFirebaseDb } from './firebase';
import { handleAuthError } from './authRecoveryService';
import type {
  Category,
  SubCategory,
  CreateCategoryInput,
  UpdateCategoryInput,
  DEFAULT_CATEGORIES,
} from '../types/settings';

// ============================================================================
// CATEGORY CRUD
// ============================================================================

/**
 * Create a new category
 */
export async function createCategory(
  orgId: string,
  input: CreateCategoryInput
): Promise<string> {
  const db = getFirebaseDb();
  const categoryRef = doc(collection(db, 'organizations', orgId, 'categories'));
  const categoryId = categoryRef.id;

  // Get current max order
  const categoriesSnapshot = await getDocs(
    collection(db, 'organizations', orgId, 'categories')
  );
  const maxOrder = categoriesSnapshot.docs.reduce(
    (max, doc) => Math.max(max, doc.data().order || 0),
    -1
  );

  // Create sub-categories with IDs
  const subCategories: SubCategory[] = (input.subCategories || []).map(
    (name, index) => ({
      id: `sub_${Date.now()}_${index}`,
      name,
    })
  );

  await setDoc(categoryRef, {
    id: categoryId,
    name: input.name,
    isDefault: false,
    itemTypes: input.itemTypes,
    subCategories,
    color: input.color || 'gray',
    order: maxOrder + 1,
    createdAt: serverTimestamp(),
  });

  return categoryId;
}

/**
 * Get all categories for an organization
 */
export async function getCategories(orgId: string): Promise<Category[]> {
  const db = getFirebaseDb();
  const categoriesRef = collection(db, 'organizations', orgId, 'categories');
  const q = query(categoriesRef, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => doc.data() as Category);
}

/**
 * Subscribe to categories (real-time updates)
 */
export function subscribeCategories(
  orgId: string,
  callback: (categories: Category[]) => void
): () => void {
  const db = getFirebaseDb();
  const categoriesRef = collection(db, 'organizations', orgId, 'categories');
  const q = query(categoriesRef, orderBy('order', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const categories = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[];
      callback(categories);
    },
    async (error) => {
      console.error('[CategoryService] Subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Update a category
 */
export async function updateCategory(
  orgId: string,
  categoryId: string,
  input: UpdateCategoryInput
): Promise<void> {
  const db = getFirebaseDb();
  await updateDoc(doc(db, 'organizations', orgId, 'categories', categoryId), {
    ...input,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a category (only if not default)
 */
export async function deleteCategory(
  orgId: string,
  categoryId: string
): Promise<void> {
  const db = getFirebaseDb();
  const categoryDoc = await getDoc(
    doc(db, 'organizations', orgId, 'categories', categoryId)
  );

  if (!categoryDoc.exists()) {
    throw new Error('Category not found');
  }

  if (categoryDoc.data().isDefault) {
    throw new Error('Cannot delete default categories');
  }

  await deleteDoc(doc(db, 'organizations', orgId, 'categories', categoryId));
}

/**
 * Reorder categories
 */
export async function reorderCategories(
  orgId: string,
  orderedIds: string[]
): Promise<void> {
  const db = getFirebaseDb();
  const batch = writeBatch(db);

  orderedIds.forEach((categoryId, index) => {
    const ref = doc(db, 'organizations', orgId, 'categories', categoryId);
    batch.update(ref, { order: index });
  });

  await batch.commit();
}

// ============================================================================
// SUB-CATEGORY MANAGEMENT
// ============================================================================

/**
 * Add a sub-category to a category
 */
export async function addSubCategory(
  orgId: string,
  categoryId: string,
  name: string
): Promise<string> {
  const db = getFirebaseDb();
  const categoryRef = doc(db, 'organizations', orgId, 'categories', categoryId);
  const categoryDoc = await getDoc(categoryRef);

  if (!categoryDoc.exists()) {
    throw new Error('Category not found');
  }

  const categoryData = categoryDoc.data() as Category;
  const subCategoryId = `sub_${Date.now()}`;

  const newSubCategory: SubCategory = {
    id: subCategoryId,
    name,
  };

  await updateDoc(categoryRef, {
    subCategories: [...(categoryData.subCategories || []), newSubCategory],
    updatedAt: serverTimestamp(),
  });

  return subCategoryId;
}

/**
 * Update a sub-category name
 */
export async function updateSubCategory(
  orgId: string,
  categoryId: string,
  subCategoryId: string,
  name: string
): Promise<void> {
  const db = getFirebaseDb();
  const categoryRef = doc(db, 'organizations', orgId, 'categories', categoryId);
  const categoryDoc = await getDoc(categoryRef);

  if (!categoryDoc.exists()) {
    throw new Error('Category not found');
  }

  const categoryData = categoryDoc.data() as Category;
  const updatedSubCategories = categoryData.subCategories.map((sub) =>
    sub.id === subCategoryId ? { ...sub, name } : sub
  );

  await updateDoc(categoryRef, {
    subCategories: updatedSubCategories,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a sub-category
 */
export async function deleteSubCategory(
  orgId: string,
  categoryId: string,
  subCategoryId: string
): Promise<void> {
  const db = getFirebaseDb();
  const categoryRef = doc(db, 'organizations', orgId, 'categories', categoryId);
  const categoryDoc = await getDoc(categoryRef);

  if (!categoryDoc.exists()) {
    throw new Error('Category not found');
  }

  const categoryData = categoryDoc.data() as Category;
  const updatedSubCategories = categoryData.subCategories.filter(
    (sub) => sub.id !== subCategoryId
  );

  await updateDoc(categoryRef, {
    subCategories: updatedSubCategories,
    updatedAt: serverTimestamp(),
  });
}

// ============================================================================
// DEFAULT CATEGORY INITIALIZATION
// ============================================================================

/**
 * Initialize default categories for a new organization
 */
export async function initializeDefaultCategories(
  orgId: string,
  defaultCategories: typeof DEFAULT_CATEGORIES
): Promise<void> {
  const db = getFirebaseDb();
  const batch = writeBatch(db);

  defaultCategories.forEach((category, index) => {
    const categoryRef = doc(collection(db, 'organizations', orgId, 'categories'));

    // Create sub-categories with IDs
    const subCategories: SubCategory[] = (category.subCategories || []).map(
      (name, subIndex) => ({
        id: `sub_${Date.now()}_${index}_${subIndex}`,
        name,
      })
    );

    batch.set(categoryRef, {
      id: categoryRef.id,
      name: category.name,
      isDefault: true,
      itemTypes: category.itemTypes,
      subCategories,
      color: 'gray',
      order: index,
      createdAt: serverTimestamp(),
    });
  });

  await batch.commit();
}

// ============================================================================
// ENSURE DEFAULT CATEGORIES (Additive sync for existing orgs)
// ============================================================================

/**
 * Ensure all DEFAULT_CATEGORIES exist for an organization.
 * Additive only: adds missing categories and missing sub-categories.
 * Never modifies or deletes existing categories/sub-categories.
 */
export async function ensureDefaultCategories(
  orgId: string,
  defaultCategories: typeof DEFAULT_CATEGORIES
): Promise<number> {
  const db = getFirebaseDb();
  const existing = await getCategories(orgId);
  const existingByName = new Map(existing.map(c => [c.name, c]));

  let addedCount = 0;

  // Find the current max order
  const maxOrder = existing.reduce((max, c) => Math.max(max, c.order ?? 0), -1);
  let nextOrder = maxOrder + 1;

  const batch = writeBatch(db);
  let batchHasWrites = false;

  for (const seed of defaultCategories) {
    const existingCat = existingByName.get(seed.name);

    if (!existingCat) {
      // Category doesn't exist — create it
      const categoryRef = doc(collection(db, 'organizations', orgId, 'categories'));
      const subCategories: SubCategory[] = (seed.subCategories || []).map(
        (name, subIndex) => ({
          id: `sub_${Date.now()}_${nextOrder}_${subIndex}`,
          name,
        })
      );

      batch.set(categoryRef, {
        id: categoryRef.id,
        name: seed.name,
        isDefault: true,
        itemTypes: seed.itemTypes,
        subCategories,
        color: 'gray',
        order: nextOrder++,
        createdAt: serverTimestamp(),
      });

      batchHasWrites = true;
      addedCount++;
    } else {
      // Category exists — check for missing sub-categories
      const existingSubNames = new Set(
        (existingCat.subCategories || []).map(s => s.name)
      );
      const missingSubs = (seed.subCategories || []).filter(
        name => !existingSubNames.has(name)
      );

      if (missingSubs.length > 0) {
        const newSubs: SubCategory[] = missingSubs.map((name, idx) => ({
          id: `sub_${Date.now()}_${idx}`,
          name,
        }));

        const categoryRef = doc(db, 'organizations', orgId, 'categories', existingCat.id);
        batch.update(categoryRef, {
          subCategories: [...(existingCat.subCategories || []), ...newSubs],
          updatedAt: serverTimestamp(),
        });

        batchHasWrites = true;
        addedCount++;
      }
    }
  }

  if (batchHasWrites) {
    await batch.commit();
  }

  return addedCount;
}

// ============================================================================
// CATEGORY HELPERS
// ============================================================================

/**
 * Get categories filtered by item type
 */
export function filterCategoriesByType(
  categories: Category[],
  itemType: string
): Category[] {
  return categories.filter((cat) => cat.itemTypes.includes(itemType as any));
}

/**
 * Get a specific category by ID
 */
export async function getCategory(
  orgId: string,
  categoryId: string
): Promise<Category | null> {
  const db = getFirebaseDb();
  const categoryDoc = await getDoc(
    doc(db, 'organizations', orgId, 'categories', categoryId)
  );

  if (!categoryDoc.exists()) {
    return null;
  }

  return categoryDoc.data() as Category;
}
